import React, { useState } from 'react';
import {
  Card,
  Input,
  Select,
  Tag,
  Statistic,
  Row,
  Col,
  Empty,
  Avatar,
  Tooltip,
  List,
  Space,
} from 'antd';
import {
  SearchOutlined,
  UserOutlined,
  BankOutlined,
  ProjectOutlined,
  FileTextOutlined,
  WarningOutlined,
  LinkOutlined,
} from '@ant-design/icons';

const { Option } = Select;

// 实体类型
interface Entity {
  id: string;
  name: string;
  type: 'ORG_BUYER' | 'ORG_BIDDER' | 'ORG_AGENT' | 'PROJECT' | 'LAW' | 'CLAUSE';
  avatar?: string;
  stats?: {
    relatedCases: number;
    winRate?: number;
    totalQuestions?: number;
  };
}

// 关系
interface EntityRelation {
  source: string;
  target: string;
  relation: string;
  description: string;
  caseId?: string;
}

// 模拟实体数据
const mockEntities: Entity[] = [
  {
    id: 'e1',
    name: '合肥科大立安安全技术有限责任公司',
    type: 'ORG_BIDDER',
    stats: { relatedCases: 15, winRate: 60, totalQuestions: 23 },
  },
  {
    id: 'e2',
    name: '云南烟叶复烤有限责任公司',
    type: 'ORG_BUYER',
    stats: { relatedCases: 8, totalQuestions: 12 },
  },
  {
    id: 'e3',
    name: '智慧消防建设项目',
    type: 'PROJECT',
    stats: { relatedCases: 3 },
  },
  {
    id: 'e4',
    name: '中华人民共和国建筑法',
    type: 'LAW',
    stats: { relatedCases: 45 },
  },
  {
    id: 'e5',
    name: '广西裕达农副产品配送有限公司',
    type: 'ORG_BIDDER',
    stats: { relatedCases: 6, winRate: 33, totalQuestions: 9 },
  },
];

// 模拟关系数据
const mockRelations: EntityRelation[] = [
  { source: 'e1', target: 'e3', relation: '参与项目', description: '参与智慧消防建设项目投标', caseId: 'CASE-2024-0002' },
  { source: 'e1', target: 'e2', relation: '提出质疑', description: '对招标结果提出质疑', caseId: 'CASE-2024-0002' },
  { source: 'e1', target: 'e4', relation: '引用法规', description: '引用建筑法第十三条', caseId: 'CASE-2024-0002' },
  { source: 'e2', target: 'e3', relation: '招标项目', description: '作为招标人发布项目', caseId: 'CASE-2024-0002' },
  { source: 'e5', target: 'e2', relation: '提出质疑', description: '对副食品配送项目提出质疑', caseId: 'CASE-2024-0001' },
];

const EntityExplorer: React.FC = () => {
  const [searchValue, setSearchValue] = useState('');
  const [selectedEntity, setSelectedEntity] = useState<Entity | null>(mockEntities[0]);
  const [relationFilter, setRelationFilter] = useState<string>('all');
  const [yearFilter, setYearFilter] = useState<string>('all');

  // 搜索实体
  const handleSearch = () => {
    const entity = mockEntities.find((e) => e.name.includes(searchValue));
    if (entity) {
      setSelectedEntity(entity);
    }
  };

  // 获取相关关系
  const getRelatedRelations = (entityId: string) => {
    return mockRelations.filter(
      (r) =>
        (r.source === entityId || r.target === entityId) &&
        (relationFilter === 'all' || r.relation === relationFilter)
    );
  };

  // 获取相关实体
  const getRelatedEntities = (entityId: string) => {
    const relations = getRelatedRelations(entityId);
    const relatedIds = relations.map((r) =>
      r.source === entityId ? r.target : r.source
    );
    return mockEntities.filter((e) => relatedIds.includes(e.id));
  };

  // 获取实体图标
  const getEntityIcon = (type: Entity['type']) => {
    switch (type) {
      case 'ORG_BUYER':
        return <BankOutlined className="text-blue-500" />;
      case 'ORG_BIDDER':
        return <UserOutlined className="text-green-500" />;
      case 'ORG_AGENT':
        return <UserOutlined className="text-purple-500" />;
      case 'PROJECT':
        return <ProjectOutlined className="text-orange-500" />;
      case 'LAW':
        return <FileTextOutlined className="text-red-500" />;
      case 'CLAUSE':
        return <WarningOutlined className="text-yellow-500" />;
      default:
        return <UserOutlined />;
    }
  };

  // 获取实体颜色
  const getEntityColor = (type: Entity['type']) => {
    const colors: Record<string, string> = {
      ORG_BUYER: 'blue',
      ORG_BIDDER: 'green',
      ORG_AGENT: 'purple',
      PROJECT: 'orange',
      LAW: 'red',
      CLAUSE: 'yellow',
    };
    return colors[type] || 'default';
  };

  const relatedEntities = selectedEntity ? getRelatedEntities(selectedEntity.id) : [];
  const relatedRelations = selectedEntity ? getRelatedRelations(selectedEntity.id) : [];

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* 顶部搜索栏 */}
      <Card className="mb-4 flex-shrink-0">
        <Row gutter={16} align="middle">
          <Col flex="1">
            <Input.Search
              placeholder="输入实体名称（如：合肥科大立安）"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onSearch={handleSearch}
              prefix={<SearchOutlined />}
              allowClear
            />
          </Col>
          <Col>
            <Select
              placeholder="关系类型"
              value={relationFilter}
              onChange={setRelationFilter}
              style={{ width: 140 }}
              allowClear
            >
              <Option value="all">全部关系</Option>
              <Option value="提出质疑">仅看质疑</Option>
              <Option value="参与项目">参与项目</Option>
              <Option value="引用法规">引用法规</Option>
            </Select>
          </Col>
          <Col>
            <Select
              placeholder="时间范围"
              value={yearFilter}
              onChange={setYearFilter}
              style={{ width: 120 }}
              allowClear
            >
              <Option value="all">全部时间</Option>
              <Option value="2024">2024年</Option>
              <Option value="2023">2023年</Option>
            </Select>
          </Col>
        </Row>
      </Card>

      {/* 主体内容 */}
      <Row gutter={16} className="flex-1 min-h-0">
        {/* 左侧：图谱画布 */}
        <Col span={16} className="h-full pr-2">
          <Card
            title="实体关系图谱"
            className="h-full"
            styles={{ body: { height: 'calc(100% - 46px)', padding: 0, overflow: 'auto' } }}
          >
            {selectedEntity ? (
              <div className="h-full relative overflow-auto">
                {/* 图谱容器 */}
                <div className="min-h-full flex items-center justify-center p-8">
                  <div className="relative" style={{ width: 600, height: 500 }}>
                    {/* 辐射关系节点 */}
                    {relatedEntities.map((entity, index) => {
                      const total = relatedEntities.length;
                      const angle = (index / total) * 2 * Math.PI - Math.PI / 2;
                      const radius = 180;
                      const centerX = 300;
                      const centerY = 250;
                      const x = centerX + Math.cos(angle) * radius - 25;
                      const y = centerY + Math.sin(angle) * radius - 25;

                      return (
                        <Tooltip
                          key={entity.id}
                          title={
                            <div>
                              <div className="font-bold">{entity.name}</div>
                              <div>类型: {entity.type}</div>
                              <div>相关案例: {entity.stats?.relatedCases || 0}</div>
                            </div>
                          }
                        >
                          <div
                            className="absolute cursor-pointer hover:scale-110 transition-transform"
                            style={{ left: x, top: y, width: 50, height: 70 }}
                            onClick={() => setSelectedEntity(entity)}
                          >
                            <div className="flex flex-col items-center">
                              <Avatar
                                size={50}
                                icon={getEntityIcon(entity.type)}
                                style={{
                                  backgroundColor: getEntityColor(entity.type),
                                }}
                              />
                              <div className="mt-1 text-xs text-center bg-white/90 px-1 rounded shadow-sm truncate w-full">
                                {entity.name.slice(0, 6)}
                                {entity.name.length > 6 ? '..' : ''}
                              </div>
                            </div>
                          </div>
                        </Tooltip>
                      );
                    })}
                    
                    {/* 中心实体 */}
                    <div 
                      className="absolute z-10"
                      style={{ left: 300 - 40, top: 250 - 50 }}
                    >
                      <div className="flex flex-col items-center">
                        <Avatar
                          size={80}
                          icon={getEntityIcon(selectedEntity.type)}
                          style={{
                            backgroundColor: getEntityColor(selectedEntity.type),
                            fontSize: 32,
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                          }}
                        />
                        <div className="mt-2 text-center bg-white/90 px-3 py-1 rounded-lg shadow-sm max-w-[160px]">
                          <div className="font-bold text-sm truncate">{selectedEntity.name}</div>
                          <Tag color={getEntityColor(selectedEntity.type)}>
                            {selectedEntity.type}
                          </Tag>
                        </div>
                      </div>
                    </div>

                    {/* 关系线 */}
                    <svg className="absolute inset-0 pointer-events-none" style={{ width: 600, height: 500 }}>
                      {relatedEntities.map((entity, index) => {
                        const total = relatedEntities.length;
                        const angle = (index / total) * 2 * Math.PI - Math.PI / 2;
                        const radius = 180;
                        const centerX = 300;
                        const centerY = 250;
                        const x1 = centerX;
                        const y1 = centerY;
                        const x2 = centerX + Math.cos(angle) * radius;
                        const y2 = centerY + Math.sin(angle) * radius;

                        return (
                          <line
                            key={entity.id}
                            x1={x1}
                            y1={y1}
                            x2={x2}
                            y2={y2}
                            stroke="#d9d9d9"
                            strokeWidth={2}
                            strokeDasharray="5,5"
                          />
                        );
                      })}
                    </svg>
                  </div>
                </div>

                {/* 关系线提示 */}
                <div className="absolute bottom-4 left-4 right-4 bg-white/90 p-3 rounded-lg shadow-sm">
                  <div className="text-sm text-gray-500 mb-2">关系说明：</div>
                  <Space wrap>
                    {Array.from(new Set(relatedRelations.map((r) => r.relation))).map(
                      (relation) => (
                        <Tag key={relation} color="blue">
                          <LinkOutlined /> {relation}
                        </Tag>
                      )
                    )}
                  </Space>
                </div>
              </div>
            ) : (
              <Empty description="搜索实体查看关系图谱" className="pt-20" />
            )}
          </Card>
        </Col>

        {/* 右侧：实体详情 */}
        <Col span={8} className="h-full overflow-auto pl-2">
          {selectedEntity ? (
            <div className="space-y-4">
              {/* 基本信息 */}
              <Card>
                <div className="flex items-center gap-3 mb-4">
                  <Avatar
                    size={64}
                    icon={getEntityIcon(selectedEntity.type)}
                    style={{
                      backgroundColor: getEntityColor(selectedEntity.type),
                    }}
                  />
                  <div>
                    <div className="font-bold text-lg">{selectedEntity.name}</div>
                    <Tag color={getEntityColor(selectedEntity.type)}>
                      {selectedEntity.type}
                    </Tag>
                  </div>
                </div>

                <Row gutter={16}>
                  <Col span={12}>
                    <Statistic
                      title="相关案例"
                      value={selectedEntity.stats?.relatedCases || 0}
                      prefix={<FileTextOutlined />}
                    />
                  </Col>
                  {selectedEntity.stats?.winRate !== undefined && (
                    <Col span={12}>
                      <Statistic
                        title="质疑成功率"
                        value={selectedEntity.stats.winRate}
                        suffix="%"
                        valueStyle={{
                          color:
                            selectedEntity.stats.winRate >= 60
                              ? '#52c41a'
                              : selectedEntity.stats.winRate >= 40
                              ? '#faad14'
                              : '#f5222d',
                        }}
                      />
                    </Col>
                  )}
                  {selectedEntity.stats?.totalQuestions && (
                    <Col span={12} className="mt-4">
                      <Statistic
                        title="发起质疑"
                        value={selectedEntity.stats.totalQuestions}
                        prefix={<WarningOutlined />}
                      />
                    </Col>
                  )}
                </Row>
              </Card>

              {/* 关系列表 */}
              <Card title="关系详情">
                <List
                 
                  dataSource={relatedRelations}
                  renderItem={(item) => (
                    <List.Item>
                      <List.Item.Meta
                        title={
                          <Space>
                            <Tag color="blue">{item.relation}</Tag>
                            <span className="text-xs text-gray-400">
                              {item.caseId}
                            </span>
                          </Space>
                        }
                        description={item.description}
                      />
                    </List.Item>
                  )}
                />
              </Card>

              {/* 高频质疑点 */}
              {selectedEntity.type === 'ORG_BIDDER' && (
                <Card title="高频质疑点">
                  <Space wrap>
                    <Tag color="red">资质排他</Tag>
                    <Tag color="orange">业绩要求</Tag>
                    <Tag color="blue">技术参数</Tag>
                  </Space>
                </Card>
              )}
            </div>
          ) : (
            <Card>
              <Empty description="选择实体查看详情" />
            </Card>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default EntityExplorer;
